import { useMemo, RefObject } from "react";
import { useAppStore } from "@/stores/appStore";
import { useFieldConfig } from "@/hooks/useFieldConfig";
import { useWindowWidth } from "@/hooks/useWindowWidth";
import { InitiativeCard } from "./InitiativeCard";
import { ICON_MAP } from "@/components/modals/IconPicker";
import { formatMonthLabel } from "@/utils/monthLabel";
import { MonthLabel } from "@/components/ui/MonthLabel";
import { ClassificationFooter } from "@/components/ui/ClassificationFooter";
import type { Initiative, SubProduct } from "@/types";

interface TimelineViewProps {
  initiatives?: Initiative[];
  containerRef?: RefObject<HTMLDivElement>;
  viewMode?: 'weekly' | 'monthly' | 'quarterly';
}

// Standard calendar quarters: Q1=Jan/Feb/Mar  Q2=Apr/May/Jun  Q3=Jul/Aug/Sep  Q4=Oct/Nov/Dec
// Each quarter spans 3 consecutive columns.
const QUARTER_GROUPS = [
  { label: 'Q1', months: ['Jan', 'Feb', 'Mar'] },
  { label: 'Q2', months: ['Apr', 'May', 'Jun'] },
  { label: 'Q3', months: ['Jul', 'Aug', 'Sep'] },
  { label: 'Q4', months: ['Oct', 'Nov', 'Dec'] },
];

// Bar height in rem (compact bar style)
const CARD_HEIGHT = 3;
const CARD_GAP = 0.5;
const ROW_PADDING = 0.75;

export function TimelineView({ initiatives: filteredInitiatives, containerRef, viewMode = 'monthly' }: TimelineViewProps) {
  const { products, initiatives: allInitiatives, quarters, openViewInitiativeModal } =
    useAppStore();
  const fc = useFieldConfig();
  const windowWidth = useWindowWidth();
  const firstColWidth = windowWidth < 1280 ? '10rem' : '15rem';
  // Occupied months get the full column width; empty months narrow to a label-only sliver.
  const fullColWidth = windowWidth < 1280 ? 160 : 200;
  const emptyColWidth = windowWidth < 1280 ? 64 : 80;

  // Use filtered initiatives if provided, otherwise use all
  const initiatives = filteredInitiatives || allInitiatives;

  // Group initiatives by sub-product and sort by startQuarter
  const initiativesBySubProduct = useMemo(() => {
    const grouped = new Map<string, Initiative[]>();
    initiatives.forEach((init) => {
      const key = init.subProductId;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(init);
    });
    // Sort each group by startQuarter displayOrder
    grouped.forEach((inits) => {
      inits.sort((a, b) => {
        const aIdx = quarters.findIndex(q => q.key === a.startQuarter);
        const bIdx = quarters.findIndex(q => q.key === b.startQuarter);
        return aIdx - bIdx;
      });
    });
    return grouped;
  }, [initiatives, quarters]);

  // Group sub-products by product
  const subProductsByProduct = useMemo(() => {
    const grouped = new Map<string, SubProduct[]>();
    products.forEach((product) => {
      if (product.subProducts) {
        grouped.set(product.id, product.subProducts);
      }
    });
    return grouped;
  }, [products]);

  // Count initiatives for a product
  const getProductInitiativeCount = (productId: string): number => {
    const subProducts = subProductsByProduct.get(productId) || [];
    return subProducts.reduce((count, sp) => {
      return count + (initiativesBySubProduct.get(sp.id)?.length || 0);
    }, 0);
  };

  const handleInitiativeClick = (initiative: Initiative) => {
    openViewInitiativeModal(initiative);
  };

  // Get quarter index for positioning
  const getQuarterIndex = (quarterKey: string) => {
    return quarters.findIndex((q) => q.key === quarterKey);
  };

  // Calculate row height based on number of initiatives
  const calculateRowHeight = (initiativeCount: number): number => {
    if (initiativeCount === 0) return 5;
    return ROW_PADDING + (initiativeCount * (CARD_HEIGHT + CARD_GAP)) + ROW_PADDING;
  };

  // Which months have ≥1 visible initiative — used to dim/narrow empty columns.
  const occupiedQuarters = useMemo(() => {
    const occupied = new Set<string>();
    initiatives.forEach((init) => {
      const si = quarters.findIndex(q => q.key === init.startQuarter);
      if (si < 0) return;
      const rawEi = init.endQuarter ? quarters.findIndex(q => q.key === init.endQuarter) : si;
      const ei = rawEi < 0 ? si : rawEi;
      for (let idx = si; idx <= ei; idx++) occupied.add(quarters[idx].key);
    });
    return occupied;
  }, [initiatives, quarters]);

  const widthOf = (quarterKey: string) => (occupiedQuarters.has(quarterKey) ? fullColWidth : emptyColWidth);

  // Cumulative left-offset (px) of each quarter — needed for card positioning with mixed column widths.
  const quarterOffsets = useMemo(() => {
    const offsets: number[] = [];
    let x = 0;
    quarters.forEach((q, i) => { offsets[i] = x; x += widthOf(q.key); });
    return offsets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quarters, occupiedQuarters, fullColWidth, emptyColWidth]);

  const gridCols = useMemo(
    () => `${firstColWidth} ${quarters.map(q => `${widthOf(q.key)}px`).join(' ')}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quarters, occupiedQuarters, firstColWidth, fullColWidth, emptyColWidth],
  );

  // Quarterly superheader cells — groups consecutive same-FY+quarter month columns, only used
  // when viewMode is 'quarterly'. Alternates two shades per quarter index; greys out if none of
  // the months in that band have any initiatives.
  const quarterlyHeaderCells = useMemo(() => {
    type Cell = { key: string; label: string; colSpan: number; colorClass: string };
    const cells: Cell[] = [];
    let i = 0;
    while (i < quarters.length) {
      const [fyLabel, monthLabel] = quarters[i].fullLabel.split(' ');
      const qg = QUARTER_GROUPS.find(g => g.months.includes(monthLabel));
      if (!qg) { i++; continue; }
      let colSpan = 1;
      let bandHasInitiatives = occupiedQuarters.has(quarters[i].key);
      let j = i + 1;
      while (j < quarters.length) {
        const [nFy, nMonth] = quarters[j].fullLabel.split(' ');
        if (nFy !== fyLabel || !qg.months.includes(nMonth)) break;
        if (occupiedQuarters.has(quarters[j].key)) bandHasInitiatives = true;
        colSpan++;
        j++;
      }
      const qIdx = QUARTER_GROUPS.findIndex(g => g.label === qg.label);
      cells.push({
        key: `${fyLabel}-${qg.label}-${i}`,
        label: qg.label,
        colSpan,
        colorClass: bandHasInitiatives
          ? (qIdx % 2 === 0 ? 'bg-blue-100 text-blue-700' : 'bg-sky-200 text-sky-800')
          : 'bg-background-secondary text-gray-300',
      });
      i = j;
    }
    return cells;
  }, [quarters, occupiedQuarters]);

  return (
    <div ref={containerRef} className="h-full flex flex-col">
      {/* Timeline Grid - Scrollable */}
      <div className="flex-1 overflow-auto scrollbar-thin">
        <div className="relative min-w-max">
          {/* Sticky Header */}
          <div className="sticky top-0 z-30">
            {/* Quarterly superheader — only shown in quarterly mode */}
            {viewMode === 'quarterly' && (
              <div className="grid gap-0" style={{ gridTemplateColumns: gridCols }}>
                <div className="sticky left-0 z-40 bg-background-secondary border-b border-r border-gray-200" />
                {quarterlyHeaderCells.map((cell) => (
                  <div
                    key={cell.key}
                    className={`${cell.colorClass} border-b border-gray-200 border-l border-gray-200 py-1.5 text-center font-semibold text-xs tracking-wide`}
                    style={{ gridColumn: `span ${cell.colSpan}` }}
                  >
                    {cell.label}
                  </div>
                ))}
              </div>
            )}

            {/* Month header row */}
            <div className="grid gap-0" style={{ gridTemplateColumns: gridCols }}>
              <div className="sticky left-0 z-40 bg-background-secondary border-b border-r border-gray-200 py-2 px-4 font-semibold text-text-primary text-sm truncate">
                {fc.label('product')} / {fc.label('subProduct')}
              </div>
              {quarters.map((quarter) => (
                <div
                  key={quarter.id}
                  className={`bg-background-secondary border-b border-l border-gray-200 py-2 px-1 text-center whitespace-nowrap overflow-hidden ${
                    occupiedQuarters.has(quarter.key) ? 'font-semibold text-text-primary text-sm' : 'font-normal text-gray-500 text-xs'
                  }`}
                >
                  <MonthLabel value={formatMonthLabel(quarter.fullLabel)} />
                </div>
              ))}
            </div>
          </div>

          {/* Products and Sub-products */}
          {products.map((product) => {
            const subProducts = subProductsByProduct.get(product.id) || [];
            const productInitiativeCount = getProductInitiativeCount(product.id);

            return (
              <div key={product.id}>
                {/* Product Header Row - Light blue background - Always show */}
                <div
                  className="grid gap-0 bg-blue-50/50 border-b border-gray-100"
                  style={{
                    gridTemplateColumns: gridCols,
                  }}
                >
                  <div className="sticky left-0 z-10 bg-blue-50/50 py-2 px-4 font-semibold text-text-primary text-sm border-r border-gray-100 flex items-center gap-2 min-w-0">
                    {product.icon && ICON_MAP[product.icon] && (
                      <span style={{ color: product.iconColor || '#6B7280' }}>
                        {(() => {
                          const IconComponent = ICON_MAP[product.icon];
                          return <IconComponent className="w-4 h-4 flex-shrink-0" />;
                        })()}
                      </span>
                    )}
                    <span className="truncate min-w-0" title={product.name}>{product.name}</span>
                    <span
                      className="text-xs font-normal text-text-secondary flex-shrink-0"
                      title="Initiatives Found"
                    >
                      - {productInitiativeCount}
                    </span>
                  </div>
                  {quarters.map((q) => (
                    <div key={q.id} className="border-l border-gray-100" />
                  ))}
                </div>

                {/* Sub-product Rows */}
                {subProducts.map((subProduct) => {
                  const allSubProductInitiatives =
                    initiativesBySubProduct.get(subProduct.id) || [];

                  // Filter to only initiatives with valid quarters in current view
                  const subProductInitiatives = allSubProductInitiatives.filter(
                    (init) => getQuarterIndex(init.startQuarter ?? '') >= 0
                  );

                  // Skip sub-product if it has no visible initiatives
                  if (subProductInitiatives.length === 0) {
                    return null;
                  }

                  const rowHeight = calculateRowHeight(subProductInitiatives.length);

                  return (
                    <div
                      key={subProduct.id}
                      className="grid gap-0 border-b border-gray-100"
                      style={{
                        gridTemplateColumns: gridCols,
                        height: `${rowHeight}rem`,
                      }}
                    >
                      {/* Sub-product name cell - Sticky */}
                      <div
                        className="sticky left-0 z-10 p-3 pl-8 bg-background-primary text-sm text-text-secondary truncate"
                        style={{
                          height: `${rowHeight}rem`,
                          boxShadow: "1px 0 0 0 rgb(243 244 246)",
                        }}
                        title={subProduct.name}
                      >
                        {subProduct.name}
                      </div>

                      {/* Timeline container for this sub-product */}
                      <div
                        className="relative"
                        style={{
                          gridColumn: `2 / span ${quarters.length}`,
                          height: `${rowHeight}rem`,
                        }}
                      >
                        {/* Quarter column dividers - absolutely positioned */}
                        <div
                          className="absolute inset-0 flex"
                          style={{ pointerEvents: "none" }}
                        >
                          {quarters.map((quarter) => (
                            <div
                              key={`bg-${quarter.id}`}
                              className={`border-l border-gray-100 ${occupiedQuarters.has(quarter.key) ? '' : 'bg-gray-50/60'}`}
                              style={{ width: `${widthOf(quarter.key)}px`, flexShrink: 0 }}
                            />
                          ))}
                        </div>

                        {/* Initiative cards - absolutely positioned */}
                        {subProductInitiatives.map((initiative, idx) => {
                          const startIndex = getQuarterIndex(
                            initiative.startQuarter ?? '',
                          );
                          const endIndex = initiative.endQuarter
                            ? getQuarterIndex(initiative.endQuarter)
                            : startIndex;
                          const validEndIndex = endIndex < 0 ? startIndex : endIndex;
                          const span = validEndIndex - startIndex + 1;

                          // Calculate position in pixels from the cumulative offset map (columns have mixed widths)
                          const leftPx = quarterOffsets[startIndex] ?? 0;
                          const widthPx = quarters
                            .slice(startIndex, validEndIndex + 1)
                            .reduce((sum, q) => sum + widthOf(q.key), 0);

                          return (
                            <div
                              key={initiative.id}
                              className="absolute box-border"
                              style={{
                                left: `${leftPx}px`,
                                width: `${widthPx}px`,
                                top: `${ROW_PADDING + idx * (CARD_HEIGHT + CARD_GAP)}rem`,
                                paddingLeft: "0.75rem",
                                paddingRight: "0.75rem",
                                height: `${CARD_HEIGHT}rem`,
                              }}
                            >
                              <InitiativeCard
                                initiative={initiative}
                                onClick={() => handleInitiativeClick(initiative)}
                                quarterSpan={span}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <ClassificationFooter />
    </div>
  );
}
